import React, { useEffect, useState, useCallback } from "react";
import { 
  Grid, Box, Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, IconButton, Typography, TextField, InputAdornment,
  CircularProgress, Pagination, Checkbox, DialogContentText,
  Tooltip, LinearProgress, Alert, Snackbar, Select, MenuItem,
  FormControl, InputLabel, Slider, Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import ImageUploading from "react-images-uploading";
import { useDispatch, useSelector } from "react-redux";
import { 
  fetchBusinessImages, 
  uploadPublicImage, 
  deleteMultipleFiles,
  setMediaData
} from "@redux/Media/action";
import { generatorMediaURL } from "@helpers/index";
import { media9codeOrCms } from "@utils/common";
import styles from "./MediaLibrary.module.scss";

interface MediaLibraryProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  onSelectImage?: (imageIdOrPath: string) => void;
  returnFullPath?: boolean;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const resizeImage = (file: File, maxWidth: number, maxHeight: number, quality: number): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      const image = new Image();
      image.onload = () => {
        let width = image.width;
        let height = image.height;
        
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(image, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) {
              const newFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve(newFile);
            } else {
              reject(new Error("Canvas to Blob failed"));
            }
          }, file.type, quality / 100);
        } else {
          reject(new Error("Canvas context failed"));
        }
      };
      image.onerror = reject;
      image.src = readerEvent.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const MediaLibrary: React.FC<MediaLibraryProps> = ({
  open,
  onClose,
  businessId,
  onSelectImage,
  returnFullPath = false
}) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<any[]>([]);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  const [resizeConfig, setResizeConfig] = useState({
    enabled: true,
    maxWidth: 1200,
    maxHeight: 1200,
    quality: 80
  });

  const businessImagesData = useSelector((state: any) => state?.toMedia?.get('businessImages')?.toJS());
  const metadata = useSelector((state: any) => state?.toMedia?.get('metadata')?.toJS()) || { total: 0, page: 1, limit: 20 };
  const isLoading = useSelector((state: any) => state?.toMedia?.get('loading'));
  const cache = useSelector((state: any) => state?.toMedia?.get('cache'));

  const businessImages = Array.isArray(businessImagesData?.files) 
    ? businessImagesData.files 
    : (Array.isArray(businessImagesData) ? businessImagesData : []);

  const [lastLoadedKey, setLastLoadedKey] = useState("");

  const loadImages = useCallback(() => {
    const cacheKey = `${businessId}-${page}-${searchTerm || ''}`;
    if (cacheKey === lastLoadedKey && !isLoading) return;

    const cacheData = cache?.get(cacheKey);

    if (cacheData) {
      dispatch(setMediaData(cacheData.toJS()));
      setLastLoadedKey(cacheKey);
      return;
    }

    const params = {
      page,
      limit: 20,
      search: searchTerm || undefined
    };
    dispatch(fetchBusinessImages(businessId, params, {}));
    setLastLoadedKey(cacheKey);
  }, [dispatch, businessId, page, searchTerm, cache, lastLoadedKey, isLoading]);

  useEffect(() => {
    if (open) {
      loadImages();
    }
  }, [open, loadImages]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const toggleImageSelection = (imageId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedImages(prev => {
      if (prev.includes(imageId)) {
        return prev.filter(id => id !== imageId);
      } else {
        return [...prev, imageId];
      }
    });
  };

  const uploadNextFile = useCallback((queue: any[], index: number) => {
    if (index >= queue.length) {
      setUploading(false);
      setUploadQueue([]);
      setCurrentUploadIndex(0);
      setUploadProgress(100);
      setLastLoadedKey("");
      loadImages();
      setTimeout(() => setUploadProgress(0), 1000);
      return;
    }
    
    setCurrentUploadIndex(index);
    let dataForm = new FormData();
    dataForm.append("files", queue[index]?.file);
    
    const onProgress = (percent: number) => {
      // Calculate overall progress
      const overallProgress = ((index / queue.length) * 100) + (percent / queue.length);
      setUploadProgress(overallProgress);
    };

    const callbacks = {
      callbackOnSuccess: () => uploadNextFile(queue, index + 1),
      callbackOnFailure: (err: any) => {
        setUploadError("Lỗi khi tải lên file: " + queue[index]?.file?.name);
        setUploading(false);
      },
    };

    dispatch(uploadPublicImage(dataForm, callbacks, onProgress, businessId));
  }, [dispatch, businessId, loadImages]);

  const handleUploadImage = async (imageList: any) => {
    if (imageList && imageList.length > 0) {
      setUploading(true);
      setUploadError(null);
      try {
        const oversizedFiles = imageList.filter((image: any) => 
          image?.file?.size > MAX_FILE_SIZE && !resizeConfig.enabled
        );
        
        if (oversizedFiles.length > 0 && !resizeConfig.enabled) {
          setUploadError("Một số file vượt quá kích thước cho phép (2MB)");
          setUploading(false);
          return;
        }
        
        let processedQueue = [...imageList];
        
        if (resizeConfig.enabled) {
          const processedImages = await Promise.all(
            imageList.map(async (imageItem: any) => {
              if (imageItem.file.size > 500 * 1024 || true) { // Always resize if enabled for consistency
                const resizedFile = await resizeImage(
                  imageItem.file,
                  resizeConfig.maxWidth,
                  resizeConfig.maxHeight,
                  resizeConfig.quality
                );
                return { ...imageItem, file: resizedFile };
              }
              return imageItem;
            })
          );
          processedQueue = processedImages;
        }
        
        setUploadQueue(processedQueue);
        uploadNextFile(processedQueue, 0);
      } catch (err) {
        setUploadError("Lỗi xử lý ảnh trước khi upload");
        setUploading(false);
      }
    }
  };

  const handleDelete = () => {
    if (selectedImages.length > 0) {
      setDeleting(true);
      const callbacks = {
        callbackOnSuccess: () => {
          setDeleting(false);
          setSelectedImages([]);
          setLastLoadedKey("");
          loadImages();
          setConfirmDelete(false);
        },
        callbackOnFailure: (err: any) => {
          setDeleting(false);
          setUploadError("Lỗi khi xóa ảnh");
        },
      };
      
      dispatch(deleteMultipleFiles(selectedImages, businessId, callbacks));
    }
  };

  const handleSelectImage = (image: any) => {
    if (onSelectImage) {
      const value = returnFullPath ? (media9codeOrCms(image.id) || "") : image.id;
      onSelectImage(value);
      onClose();
    }
  };

  const handleChangeResizeConfig = (field: string, value: any) => {
    setResizeConfig(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        className={styles.Dialog}
      >
        <DialogTitle className={styles.DialogTitle} component="div">
          <div style={{ fontSize: '1.25rem', fontWeight: 500 }}>Thư viện Media</div>
          <IconButton onClick={onClose} size="small" className={styles.CloseButton}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          <Box className={styles.SearchAndUpload}>
            <TextField
              placeholder="Tìm kiếm ảnh..."
              size="small"
              className={styles.SearchField}
              value={searchTerm || ""}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <ImageUploading
              multiple
              value={[]}
              onChange={handleUploadImage}
              dataURLKey="data_url"
            >
              {({ onImageUpload }) => (
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={onImageUpload}
                  disabled={uploading}
                >
                  Tải lên
                </Button>
              )}
            </ImageUploading>
            
            {selectedImages.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDelete(true)}
                disabled={deleting}
              >
                Xóa ({selectedImages.length})
              </Button>
            )}
          </Box>

          <Box className={styles.ResizeConfigBox}>
            <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Tự động tối ưu ảnh:</Typography>
                <Checkbox
                  size="small"
                  checked={resizeConfig.enabled}
                  onChange={(e) => handleChangeResizeConfig('enabled', e.target.checked)}
                />
              </Stack>
              
              {resizeConfig.enabled && (
                <>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Kích thước tối đa</InputLabel>
                    <Select
                      label="Kích thước tối đa"
                      value={resizeConfig.maxWidth}
                      onChange={(e) => {
                        handleChangeResizeConfig('maxWidth', e.target.value);
                        handleChangeResizeConfig('maxHeight', e.target.value);
                      }}
                    >
                      <MenuItem value={800}>800px</MenuItem>
                      <MenuItem value={1024}>1024px</MenuItem>
                      <MenuItem value={1200}>1200px (Khuyên dùng)</MenuItem>
                      <MenuItem value={1600}>1600px</MenuItem>
                      <MenuItem value={2048}>2048px</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Box sx={{ width: 200 }}>
                    <Typography variant="caption">Chất lượng: {resizeConfig.quality}%</Typography>
                    <Slider
                      value={resizeConfig.quality}
                      min={30}
                      max={100}
                      step={5}
                      size="small"
                      onChange={(_, value) => handleChangeResizeConfig('quality', value)}
                    />
                  </Box>
                </>
              )}
            </Stack>
          </Box>

          {uploading && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" align="center" display="block" sx={{ mt: 0.5 }}>
                Đang tải lên {currentUploadIndex + 1}/{uploadQueue.length}... ({Math.round(uploadProgress)}%)
              </Typography>
            </Box>
          )}

          <Box className={styles.ImagesContainer}>
            {isLoading ? (
              <Box className={styles.LoadingContainer}>
                <CircularProgress />
              </Box>
            ) : businessImages.length > 0 ? (
              <Grid container spacing={2}>
                {businessImages.map((image: any) => (
                  <Grid item xs={6} sm={4} md={3} key={image.id}>
                    <Box 
                      className={`${styles.ImageItem} ${selectedImages.includes(image.id) ? styles.Selected : ''}`}
                    >
                      <Box className={styles.ImageActions}>
                        <Checkbox
                          checked={selectedImages.includes(image.id)}
                          onClick={(e) => toggleImageSelection(image.id, e)}
                          size="small"
                          icon={<RadioButtonUncheckedIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                          checkedIcon={<CheckCircleIcon color="primary" />}
                        />
                      </Box>
                      <Box className={styles.ImageContainer} onClick={() => handleSelectImage(image)}>
                        <img 
                          src={media9codeOrCms(image.id) || ""} 
                          alt={image.name || "Image"} 
                          loading="lazy"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=Error';
                          }}
                        />
                      </Box>
                      <Tooltip title={image.id}>
                        <Box className={styles.ImagePath}>
                          {image.id}
                        </Box>
                      </Tooltip>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box className={styles.NoImages}>
                <ImageNotSupportedIcon />
                <Typography>Không tìm thấy ảnh nào</Typography>
              </Box>
            )}
          </Box>

          {metadata.total > 20 && (
            <Box className={styles.Pagination}>
              <Pagination 
                count={Math.ceil(metadata.total / 20)} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Hủy</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bạn có chắc chắn muốn xóa {selectedImages.length} ảnh đã chọn? Hành động này không thể hoàn tác.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Hủy</Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : "Xóa"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar 
        open={!!uploadError} 
        autoHideDuration={6000} 
        onClose={() => setUploadError(null)}
      >
        <Alert onClose={() => setUploadError(null)} severity="error" sx={{ width: '100%' }}>
          {uploadError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MediaLibrary;
